import { decryptSharedItem, encryptSharedItem } from '@heybesafe/shared';
import type { CategorySummary, VaultItem } from '@heybesafe/shared';

/** The plaintext behind a category's `encryptedName` blob. */
export interface DecryptedCategoryName {
  name: string;
  icon?: string;
}

/**
 * Decrypt a category's ECIES-wrapped name. Category names use the **same
 * `share-item-v1` scheme** as shared vault items (foundation spec §5.2), so we
 * reuse `decryptSharedItem` by presenting the name blob as an
 * `EncryptedVaultItem`. The decrypted JSON is `{ name, icon? }` — not a full
 * item — so we read just those fields off the result. Keys are the same family
 * keypair used to decrypt shared items: the private key from
 * `FamilyKeysContext.getKey(orgId)`, the public key from `org.publicKey`.
 */
export async function decryptCategoryName(
  summary: CategorySummary,
  familyPrivateKey: Uint8Array,
  familyPublicKey: Uint8Array,
): Promise<DecryptedCategoryName> {
  const decoded = (await decryptSharedItem(
    {
      id: summary.categoryId,
      ciphertext: summary.encryptedName,
      iv: summary.encryptedNameIv,
      clientUpdatedAt: '',
      ephemeralPublicKey: summary.ephemeralPublicKey,
    },
    familyPrivateKey,
    familyPublicKey,
  )) as unknown as DecryptedCategoryName;
  return { name: decoded.name, icon: decoded.icon };
}

/** The three record fields produced by encrypting a category's name. */
export interface EncryptedCategoryName {
  encryptedName: string;
  encryptedNameIv: string;
  ephemeralPublicKey: string;
}

/**
 * Encrypt a category's `{ name, icon? }` to the family public key — the inverse
 * of `decryptCategoryName`, and the write half of category management. Reuses
 * `encryptSharedItem` (the `share-item-v1` scheme) by presenting the name blob
 * as a `VaultItem`; only its `ciphertext` / `iv` / `ephemeralPublicKey` outputs
 * are kept and mapped to the category-record fields.
 */
export async function encryptCategoryName(
  name: string,
  icon: string | undefined,
  familyPublicKey: Uint8Array,
): Promise<EncryptedCategoryName> {
  const payload = { name, ...(icon ? { icon } : {}) };
  const enc = await encryptSharedItem(payload as unknown as VaultItem, familyPublicKey);
  return {
    encryptedName: enc.ciphertext,
    encryptedNameIv: enc.iv,
    ephemeralPublicKey: enc.ephemeralPublicKey!,
  };
}
